import { useCallback } from 'react';
import { useStore } from '@nanostores/react';
import type { CopilotAgentUsageMeterBody } from '@customerio/hydra-ui-api';
import { authStore } from '@/services/auth/store';
import type { AgentUsageCommonProperty } from '@/services/analytics/types';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { isStandardRole, useUserRoleForWorkspace } from '@/features/roles';
import { agentUsedPercent } from '../components/agent-usage-copy';

/** Percent-range buckets keep event cardinality sane; raw values stay server-side in copilot_usage. */
export function agentUsagePercentBucket(percent: number): string {
  if (percent >= 100) {
    return '100+';
  }
  if (percent >= 75) {
    return '75-100';
  }
  if (percent >= 50) {
    return '50-75';
  }
  if (percent >= 25) {
    return '25-50';
  }
  return '0-25';
}

/** 1-based day of the billing cycle, so "how deep into the cycle" is sliceable without date math. */
export function agentUsageCycleDay(meter: CopilotAgentUsageMeterBody, now: number = Date.now()): number {
  const cycleStart = new Date(meter.cycle_start).getTime();
  if (Number.isNaN(cycleStart) || now < cycleStart) {
    return 1;
  }
  return Math.floor((now - cycleStart) / 86_400_000) + 1;
}

type AgentUsageCommonEventValue = string | number | boolean | null;
export type AgentUsageCommonEventProps = Record<AgentUsageCommonProperty, AgentUsageCommonEventValue>;

/**
 * Builds the property set shared by every `agentUsage` analytics event, so the
 * pricing-experiment analysis can slice all surfaces the same way: usage bucket,
 * 1A/1B enforcement era (`Enforcing`/`Beta` come straight off the meter), and
 * role. `Role` is the raw per-workspace role name — custom roles pass through —
 * with `Is Standard Role` alongside to bucket custom roles together; both are
 * null when the user has no role in the current workspace.
 */
export function useAgentUsageCommonEventProps() {
  const auth = useStore(authStore);
  // Resolve the workspace the same way useAnalytics does (context with last/default
  // fallback), not from the URL: settings/billing routes have no /workspaces/<id>
  // segment, so the URL-derived role would always be null on those surfaces.
  const workspace = useCurrentWorkspace();
  const role = useUserRoleForWorkspace(workspace?.id ?? null);
  const accountId = auth.account?.id ?? null;
  const roleName = role?.name ?? null;

  return useCallback(
    (meter: CopilotAgentUsageMeterBody) =>
      ({
        'Used Percent Bucket': agentUsagePercentBucket(agentUsedPercent(meter)),
        // projected_percent arrives unclamped, so the projection can land in 100+.
        'Projected Percent Bucket': agentUsagePercentBucket(Math.round(meter.projected_percent * 100)),
        Enforcing: meter.enforcing,
        Beta: meter.beta,
        'Account Id': accountId,
        Role: roleName,
        'Is Standard Role': roleName == null ? null : isStandardRole(roleName),
      }) satisfies AgentUsageCommonEventProps,
    [accountId, roleName],
  );
}
